import { useEffect, useState } from "react";
import { Button, Empty, Input, Pagination, Spin, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "@layouts/PageLayout";
import { addToCart } from "@store/cartSlice";
import { useAppDispatch } from "@store/hooks";
import { formatInr, getProducts } from "@services/shop.service";
import { PAGE_SIZE } from "./list";
import "./shop.css";

const fallbackImage = "https://placehold.co/600x400?text=Product";

function ShopPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState("");
  const [draft, setDraft] = useState(search);

  useEffect(() => {
    setDraft(search);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        const term = search.trim();
        if (term) queryParams.set("search", term);
        if (category) queryParams.set("category", category);
        queryParams.set("page", String(page));
        queryParams.set("page_size", String(PAGE_SIZE));
        const resp = await getProducts(`?${queryParams}`);
        if (cancelled) return;
        const list = Array.isArray(resp?.products) ? resp.products : [];
        setProducts(list.filter((product: { is_active?: boolean }) => product.is_active !== false));
        setCategories((Array.isArray(resp?.categories) ? resp.categories : []).filter(Boolean));
        setPageSize(Number(resp?.page_size) || PAGE_SIZE);
        setTotal(Number(resp?.total) || 0);
      } catch (error) {
        if (!cancelled) message.error("Could not load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [search, category, page]);

  const addProduct = async (productId: string) => {
    try {
      setLoadingId(productId);
      await dispatch(addToCart({ product_id: productId, quantity: 1 })).unwrap();
      message.success("Added to cart");
    } catch (error) {
      message.error("Could not add this product");
    } finally {
      setLoadingId("");
    }
  };

  return (
    <PageLayout>
      <div className="shop-page">
        <div className="shop-hero">
          <div>
            <h2>Shop</h2>
            <p>Browse without an account. Sign in only when you pay.</p>
          </div>
          <Input.Search
            className="shop-search"
            allowClear
            placeholder="Search name, SKU, or description"
            value={draft}
            onChange={(event) => {
              const next = event.target.value;
              setDraft(next);
              if (!next) {
                setPage(1);
                setParams({});
              }
            }}
            onSearch={(value) => {
              const term = value.trim();
              setPage(1);
              setParams(term ? { q: term } : {});
            }}
          />
        </div>
        <div className="toolbar">
          <button className={`chip ${!category ? "active" : ""}`} onClick={() => { setCategory(""); setPage(1); }}>
            All
          </button>
          {categories.map((item) => (
            <button
              key={item}
              className={`chip ${category === item ? "active" : ""}`}
              onClick={() => { setCategory(item); setPage(1); }}
            >
              {item}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="surface" style={{ textAlign: "center", padding: 48 }}><Spin /></div>
        ) : products.length === 0 ? (
          <div className="surface">
            <Empty description={search || category ? "No products match that search" : "No products yet"}>
              {search || category ? (
                <Button onClick={() => { setCategory(""); setPage(1); setParams({}); }}>Clear filters</Button>
              ) : null}
            </Empty>
          </div>
        ) : (
          <>
            <div className="product-grid">
              {products.map((product) => (
                <article className="product-card" key={product.id}>
                  <img
                    src={product.image_url || fallbackImage}
                    alt={product.name}
                    onError={(event) => { event.currentTarget.src = fallbackImage; }}
                    onClick={() => navigate(`/product/${product.id}`)}
                    style={{ cursor: "pointer" }}
                  />
                  <div className="body">
                    <div className="muted">{[product.category || "General", product.sku].filter(Boolean).join(" · ")}</div>
                    <strong>{product.name}</strong>
                    <div className="price-row">
                      <strong>{formatInr(product.price)}</strong>
                      {Number(product.compare_at_price) > Number(product.price) ? (
                        <span className="compare">{formatInr(product.compare_at_price)}</span>
                      ) : null}
                    </div>
                    <div className="muted">
                      {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
                      {product.rating ? ` · ${Number(product.rating).toFixed(1)}` : ""}
                    </div>
                    <div className="actions">
                      <Button onClick={() => navigate(`/product/${product.id}`)}>View</Button>
                      <Button
                        type="primary"
                        loading={loadingId === product.id}
                        disabled={product.stock < 1}
                        onClick={() => addProduct(product.id)}
                      >
                        Add to cart
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="list-footer">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                hideOnSinglePage={false}
                showSizeChanger={false}
                showTotal={(count, range) => (count ? `${range[0]}–${range[1]} of ${count}` : "0")}
                onChange={setPage}
              />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default ShopPage;
